/**
 * DeviceExecutionLog Component
 *
 * Displays real-time execution steps and logs from ESP32 devices or server workflows.
 * Shows workflow execution progress for debugging and monitoring.
 *
 * For ESP32: pass deviceId
 * For Server workflows: pass executionId
 */

import { useEffect, useRef, useState } from 'react';
import { useDeviceStore, ExecutionStep, ExecutionLog } from '@/stores/devices/device-store';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Play, AlertCircle, CheckCircle, Clock, SkipForward, Terminal, Server, Cpu } from 'lucide-react';

interface DeviceExecutionLogProps {
  deviceId?: string;      // ESP32 device ID
  executionId?: string;   // Server workflow execution ID
  maxHeight?: string;
  title?: string;         // Custom title (e.g., "Server Execution")
}

export function DeviceExecutionLog({
  deviceId,
  executionId,
  maxHeight = '400px',
  title
}: DeviceExecutionLogProps) {
  const { executionSteps, executionLogs, clearExecutionLogs } = useDeviceStore();
  const [activeTab, setActiveTab] = useState<'steps' | 'logs'>('steps');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Key can be deviceId (ESP32) or executionId (server workflow)
  const key = deviceId || executionId || '';
  const isServerExecution = !!executionId && !deviceId;

  // Get logs for this device/execution
  const steps = executionSteps.get(key) || [];
  const logs = executionLogs.get(key) || [];

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [steps, logs]);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getStepIcon = (status: ExecutionStep['status']) => {
    switch (status) {
      case 'started':
        return <Play className="h-3 w-3 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      case 'skipped':
        return <SkipForward className="h-3 w-3 text-yellow-500" />;
      default:
        return <Clock className="h-3 w-3 text-gray-500" />;
    }
  };

  const getLogLevelColor = (level: ExecutionLog['level']) => {
    switch (level) {
      case 'error':
        return 'text-red-500 bg-red-500/10';
      case 'warn':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'info':
        return 'text-blue-500 bg-blue-500/10';
      case 'debug':
        return 'text-gray-500 bg-gray-500/10';
      default:
        return 'text-gray-500';
    }
  };

  const hasContent = steps.length > 0 || logs.length > 0;

  // Determine display title
  const displayTitle = title || (isServerExecution ? 'Server Execution' : 'Execution Log');
  const HeaderIcon = isServerExecution ? Server : Cpu;

  return (
    <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-800/50">
        <div className="flex items-center gap-2">
          <HeaderIcon className="h-4 w-4 text-zinc-400" />
          <span className="text-sm font-medium text-zinc-200">{displayTitle}</span>
          {hasContent && (
            <Badge variant="secondary" className="text-xs">
              {steps.length} steps / {logs.length} logs
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Tab buttons */}
          <div className="flex rounded-md bg-zinc-900 p-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('steps')}
              className={`h-6 px-2 text-xs ${
                activeTab === 'steps'
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Steps
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('logs')}
              className={`h-6 px-2 text-xs ${
                activeTab === 'logs'
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Logs
            </Button>
          </div>
          {hasContent && key && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearExecutionLogs(key)}
              className="h-6 px-2 text-zinc-400 hover:text-white"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <ScrollArea style={{ maxHeight }} ref={scrollRef}>
        <div className="p-2 font-mono text-xs">
          {activeTab === 'steps' ? (
            steps.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-zinc-500">
                <div className="text-center">
                  <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No execution steps yet</p>
                  <p className="text-xs mt-1">
                    {isServerExecution
                      ? 'Run the workflow with debug mode to see execution progress'
                      : 'Deploy a workflow to see execution progress'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {steps.map((step, index) => (
                  <div
                    key={`${step.nodeId}-${step.timestamp}-${index}`}
                    className={`flex items-start gap-2 px-2 py-1 rounded ${
                      step.status === 'error' ? 'bg-red-500/10' : ''
                    }`}
                  >
                    <span className="text-zinc-500 shrink-0">
                      {formatTimestamp(step.timestamp)}
                    </span>
                    <span className="shrink-0">{getStepIcon(step.status)}</span>
                    <span className="text-zinc-300">
                      <span className="text-blue-400">{step.nodeType}</span>
                      <span className="text-zinc-500">[{step.nodeId}]</span>
                      {' '}
                      <span className={
                        step.status === 'completed' ? 'text-green-400' :
                        step.status === 'error' ? 'text-red-400' :
                        step.status === 'started' ? 'text-blue-400' :
                        'text-yellow-400'
                      }>
                        {step.status}
                      </span>
                      {step.message && (
                        <span className="text-zinc-400"> - {step.message}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )
          ) : (
            logs.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-zinc-500">
                <div className="text-center">
                  <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No logs yet</p>
                  <p className="text-xs mt-1">Device logs will appear here</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {logs.map((log, index) => (
                  <div
                    key={`${log.timestamp}-${index}`}
                    className={`flex items-start gap-2 px-2 py-1 rounded ${getLogLevelColor(log.level)}`}
                  >
                    <span className="text-zinc-500 shrink-0">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <Badge
                      variant="outline"
                      className={`shrink-0 text-[10px] h-4 px-1 ${getLogLevelColor(log.level)}`}
                    >
                      {log.level.toUpperCase()}
                    </Badge>
                    <span className="text-zinc-300">{log.message}</span>
                    {log.data && (
                      <span className="text-zinc-500">
                        {JSON.stringify(log.data)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default DeviceExecutionLog;
